import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

const STEP_LABELS = {
  ao: 'Avant-Vente', setup: 'Setup', terrain: 'Terrain', reporting: 'Reporting', closing: 'Closing',
};
const STEPS_ORDER = ['ao', 'setup', 'terrain', 'reporting', 'closing'];

function formatDeadline(date) {
  if (!date) return null;
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const StepTransitionModal = ({ isOpen, onClose, onConfirm, project, fromStep, toStep, loading }) => {
  const [notes, setNotes] = useState('');

  const handleClose = () => { setNotes(''); onClose(); };
  // Carries a notes field: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(handleClose, { backdropClose: false, enabled: isOpen && !!project });

  if (!isOpen || !project) return null;

  const isBackward = STEPS_ORDER.indexOf(toStep) < STEPS_ORDER.indexOf(fromStep);
  const toDeadline = project[`${toStep}_deadline`];

  const handleConfirm = () => { onConfirm({ notes: notes.trim() || null }); setNotes(''); };
  const canConfirm = !loading;

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>
            {isBackward ? 'Retour en arrière' : 'Changer d\'étape'}
          </span>
          <button onClick={handleClose} disabled={loading} style={closeBtn}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20 }}>
          {/* Transition line */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 20 }}>
            <div style={stepPill(false)}>{STEP_LABELS[fromStep]}</div>
            <div style={{ width: 32, height: 1, background: '#d1d5db' }} />
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626', flexShrink: 0 }} />
            <div style={{ width: 32, height: 1, background: '#dc2626' }} />
            <div style={stepPill(true)}>{STEP_LABELS[toStep]}</div>
          </div>

          {/* Project name + deadline */}
          <div style={{ fontSize: '0.82rem', color: '#374151', marginBottom: 4 }}>
            <strong>{project.name}</strong>
          </div>
          {toStep !== 'closing' && toDeadline && (
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 5 }}>
              <FontAwesomeIcon icon={faCalendarAlt} size="xs" style={{ opacity: 0.5 }} />
              Échéance {STEP_LABELS[toStep]}: {formatDeadline(toDeadline)}
            </div>
          )}

          {isBackward && (
            <div style={{
              padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a',
              borderRadius: 8, fontSize: '0.78rem', color: '#92400e', marginBottom: 14,
            }}>
              Un commentaire est requis pour revenir en arrière.
            </div>
          )}

          <div>
            <div style={fieldLabel}>
              Commentaire {isBackward && <span style={{ color: '#dc2626' }}>*</span>}
            </div>
            <textarea
              rows={3} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={isBackward ? 'Raison du retour...' : 'Optionnel...'}
              style={textarea}
              onFocus={e => e.target.style.borderColor = '#dc2626'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={footer}>
          <button onClick={handleClose} disabled={loading} style={cancelBtn}>Annuler</button>
          <button onClick={handleConfirm} disabled={!canConfirm}
            style={{ ...confirmBtn, background: canConfirm ? '#dc2626' : '#e5e7eb', color: canConfirm ? '#fff' : '#9ca3af', cursor: canConfirm ? 'pointer' : 'default' }}>
            {loading ? 'En cours...' : 'Confirmer'}
          </button>
        </div>
      </div>
      <style>{anim('stepModalIn')}</style>
    </div>
  );
};

// ── Shared styles ──
const overlay = {
  position: 'fixed', inset: 0, zIndex: 9999,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(0,0,0,0.4)',
};
const modal = {
  background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440,
  boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden',
  animation: 'stepModalIn 0.2s ease',
};
const header = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 20px', borderBottom: '1px solid #f3f4f6',
};
const footer = {
  display: 'flex', justifyContent: 'flex-end', gap: 8,
  padding: '14px 20px', borderTop: '1px solid #f3f4f6',
};
const closeBtn = { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 };
const cancelBtn = { padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' };
const confirmBtn = { padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: '0.78rem', fontWeight: 600 };
const fieldLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#374151', marginBottom: 5 };
const textarea = {
  width: '100%', fontSize: '0.82rem', padding: '8px 12px',
  border: '1px solid #d1d5db', borderRadius: 6, outline: 'none',
  resize: 'vertical', transition: 'border-color 0.15s',
};
const stepPill = (active) => ({
  fontSize: '0.72rem', fontWeight: 600, padding: '5px 14px', borderRadius: 20,
  background: active ? '#111827' : '#f3f4f6',
  color: active ? '#fff' : '#6b7280',
  whiteSpace: 'nowrap',
});
const anim = (name) => `@keyframes ${name} { from { opacity: 0; transform: translateY(10px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`;

export default StepTransitionModal;
